import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TeamOutlined,
  LineChartOutlined,
  KeyOutlined,
  SafetyOutlined,
  ArrowRightOutlined,
  StockOutlined
} from '@ant-design/icons';

interface EnterpriseDashboardProps {
  tenantName: string;
}

export const EnterpriseDashboard: React.FC<EnterpriseDashboardProps> = ({ tenantName }) => {
  const navigate = useNavigate();

  const stats = [
    { label: 'Total Candidates Screened', value: '42', icon: <TeamOutlined style={{ fontSize: 20, color: '#4f46e5' }} />, change: '+8 this week' },
    { label: 'API Keys Active', value: '2', icon: <KeyOutlined style={{ fontSize: 20, color: '#059669' }} />, change: 'Healthy' },
    { label: 'HRIS Integration Sync', value: 'Active', icon: <SafetyOutlined style={{ fontSize: 20, color: '#2563eb' }} />, change: 'Odoo Connected' },
    { label: 'Platform Reliability', value: '99.98%', icon: <LineChartOutlined style={{ fontSize: 20, color: '#7c3aed' }} />, change: '0 errors' }
  ];

  return (
    <div className="space-y-6">
      {/* Top Banner */}
      <div className="bg-slate-900 text-white rounded-xl p-6 border-2 border-slate-950 shadow-md relative overflow-hidden">
        <div className="relative z-10 space-y-2">
          <span className="text-xs font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/35 px-2.5 py-1 rounded-full uppercase tracking-wider">
            Workspace: {tenantName}
          </span>
          <h1 className="text-2xl font-black tracking-tight pt-1">
            Enterprise Candidate Screening Portal
          </h1>
          <p className="text-slate-400 text-sm max-w-xl">
            Welcome to the HR management dashboard. Screen candidate profiles, analyze team elemental balance, and manage developer keys.
          </p>
        </div>
        <div className="absolute right-4 bottom-0 opacity-10 pointer-events-none hidden md:block">
          <StockOutlined style={{ fontSize: 240, color: '#818cf8' }} />
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <div key={idx} className="bg-white border border-slate-200 p-5 rounded-lg shadow-sm flex justify-between items-start">
            <div className="space-y-2">
              <span className="text-xs font-bold text-slate-500 block uppercase tracking-wider">{stat.label}</span>
              <span className="text-2xl font-black text-slate-900 block">{stat.value}</span>
              <span className="text-xs font-bold text-slate-400 block">{stat.change}</span>
            </div>
            <div className="p-2.5 bg-slate-50 rounded border border-slate-200">
              {stat.icon}
            </div>
          </div>
        ))}
      </div>

      {/* Action links */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Screening card */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col justify-between shadow-sm space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-bold text-slate-900">Candidate Screening</h3>
            <p className="text-sm text-slate-500">
              Add individual job applicants manually or import a batch CSV list to automatically compute alignment scores and self-clash profiles.
            </p>
          </div>
          <button 
            onClick={() => navigate('/enterprise/candidates')}
            className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm rounded shadow flex items-center justify-center gap-1.5 transition-colors"
          >
            Open Screening Workspace
            <ArrowRightOutlined style={{ fontSize: 16 }} />
          </button>
        </div>

        {/* API keys card */}
        <div className="bg-white border border-slate-200 rounded-lg p-5 flex flex-col justify-between shadow-sm space-y-4">
          <div className="space-y-2">
            <h3 className="text-lg font-bold text-slate-900">Developer Portal</h3>
            <p className="text-sm text-slate-500">
              Generate credentials, configure endpoints, and view usage events logs for Odoo, Talenta, or other custom HRIS integrations.
            </p>
          </div>
          <button 
            onClick={() => navigate('/enterprise/api-keys')}
            className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm rounded shadow flex items-center justify-center gap-1.5 transition-colors"
          >
            Manage API Credentials
            <ArrowRightOutlined style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>
    </div>
  );
};
export default EnterpriseDashboard;
